/**
 * useAdsGeo / useAdsWidgetData — выбор стран раздела «Реклама»
 * ============================================================
 *
 * Гео здесь — параметр РАЗДЕЛА, как и в ретеншене: один выбор над вкладками
 * действует на все рекламные виджеты и переживает перезагрузку (localStorage
 * `ads:geo`). Пустой выбор = все страны.
 *
 * Состояние живёт в ОДНОМ месте — AdsGeoProvider вокруг раздела. Отдельный
 * useLocalStorage в каждом виджете не годится: запись в localStorage не
 * оповещает другие экземпляры на той же вкладке, и виджеты не видели бы смены
 * выбора до перемонтирования.
 *
 * useAdsWidgetData — useWidgetData для рекламных виджетов: подмешивает выбор в
 * filters (`adsGeos`, его читает toAdsParams) и в зависимости перезапроса.
 * Виджет, загружающий данные мимо этой обёртки, фильтр стран молча потеряет.
 */

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useWidgetData } from './useWidgetData';
import { useRetentionGeo } from './useRetentionGeo';

const STORAGE_KEY = 'ads:geo';

/** Тот же предел, что у сервера (MAX_GEOS в server/src/services/ads/adsCommon.ts). */
export const ADS_MAX_GEOS = 20;

const NO_GEO = { geos: [], geoKey: '', toggleGeo: () => {}, clearGeos: () => {}, maxGeos: ADS_MAX_GEOS };

const AdsGeoContext = createContext(NO_GEO);

export function AdsGeoProvider({ children }) {
  const { geos, geoKey, toggleGeo, clearGeos } = useRetentionGeo(STORAGE_KEY);

  // Сверх предела сервер молча отрезал бы хвост — не даём выбрать лишнее.
  const toggleCapped = useCallback(
    (geo) => {
      const code = String(geo || '').trim().toUpperCase();
      if (!geos.includes(code) && geos.length >= ADS_MAX_GEOS) return;
      toggleGeo(code);
    },
    [geos, toggleGeo]
  );

  const value = useMemo(
    () => ({ geos, geoKey, toggleGeo: toggleCapped, clearGeos, maxGeos: ADS_MAX_GEOS }),
    [geos, geoKey, toggleCapped, clearGeos]
  );

  return <AdsGeoContext.Provider value={value}>{children}</AdsGeoContext.Provider>;
}

/** Выбор стран раздела. Вне AdsGeoProvider — все страны. */
export function useAdsGeo() {
  return useContext(AdsGeoContext);
}

export function useAdsWidgetData(fetchFunction, dependencies = []) {
  const { geos, geoKey } = useAdsGeo();
  const fetchWithGeo = (filters, options) => fetchFunction({ ...filters, adsGeos: geos }, options);
  // useWidgetData подписывает замер времени именем фетчера.
  Object.defineProperty(fetchWithGeo, 'name', { value: fetchFunction.name || 'widget' });
  return useWidgetData(fetchWithGeo, [geoKey, ...dependencies]);
}

export default useAdsGeo;
